import React from 'react';
import { Download, ExternalLink, Copy, CheckCircle2 } from 'lucide-react';
import SEO from '../components/SEO';

/**
 * D-071A · PAYMENTS_LOGO_V1 download centre (review-only · noindex).
 * Direct preview + download links for every payment-surface asset, plus
 * the Stripe upload matrix telling the founder exactly which file goes
 * into which Stripe surface.
 */

const BASE = '/brand/payments';

const ASSETS = [
  {
    file: 'giwahs-stripe-source.png',
    label: 'Canonical source',
    spec: '1536 × 1024 · PNG · RGB · 668 KB',
    purpose: 'Reference only. Untouched founder-approved source.',
    bgClass: 'bg-[#0A0A0A]',
    accent: 'silver',
  },
  {
    file: 'giwahs-stripe-on-dark.png',
    label: 'On-dark (default)',
    spec: '1536 × 1024 · PNG · RGB · 557 KB',
    purpose: 'Default Stripe asset. Use on dark backgrounds — checkout, customer portal, receipts.',
    bgClass: 'bg-[#0A0A0A]',
    accent: 'gold',
  },
  {
    file: 'giwahs-stripe-on-light.png',
    label: 'On-light frame',
    spec: '1844 × 1392 · PNG · RGB · 603 KB',
    purpose: 'Canonical art on #F5F5F0 frame. Invoice letterhead. Light-themed surfaces.',
    bgClass: 'bg-[#F5F5F0]',
    accent: 'gold',
  },
  {
    file: 'giwahs-stripe-transparent.png',
    label: 'Transparent',
    spec: '1536 × 1024 · PNG · RGBA · 621 KB',
    purpose: 'Alpha-keyed. Use over photography, gradients, or dynamic backgrounds.',
    bgClass: 'bg-gradient-to-br from-[#DAA520] via-[#0A0A0A] to-[#C0C0C0]',
    accent: 'transparent',
  },
  {
    file: 'giwahs-stripe.svg',
    label: 'SVG (raster-embedded)',
    spec: 'viewBox 1536 × 1024 · 829 KB',
    purpose: 'Infinite-scale web / HTML email. Avoid Outlook 2016 (defaults to 512 PNG instead).',
    bgClass: 'bg-[#0A0A0A]',
    accent: 'silver',
  },
  {
    file: 'giwahs-stripe-1024.png',
    label: '1024 px PNG',
    spec: '1024 × 683 · PNG · RGB · 297 KB',
    purpose: 'Invoice PDFs, customer portal headers, mid-resolution print.',
    bgClass: 'bg-[#0A0A0A]',
    accent: 'silver',
  },
  {
    file: 'giwahs-stripe-512.png',
    label: '512 px PNG',
    spec: '512 × 341 · PNG · RGB · 77 KB',
    purpose: 'Email signatures, transactional headers, low-bandwidth use.',
    bgClass: 'bg-[#0A0A0A]',
    accent: 'silver',
  },
  {
    file: 'giwahs-stripe-square-512.png',
    label: 'Square 512 px',
    spec: '512 × 512 · PNG · RGB · 67 KB',
    purpose: 'Mandatory square. Stripe Account avatar / Connect logo slot.',
    bgClass: 'bg-[#0A0A0A]',
    accent: 'gold',
  },
];

const STRIPE_UPLOADS = [
  {
    surface: 'Stripe Account · Avatar / Connect logo',
    path: 'Dashboard → Settings → Business settings → Branding → Icon',
    file: 'giwahs-stripe-square-512.png',
    why: 'Stripe requires a square 512 × 512 image. This is the only file that meets the constraint.',
    priority: 'P0',
  },
  {
    surface: 'Stripe Branding · Logo (wide)',
    path: 'Dashboard → Settings → Business settings → Branding → Logo',
    file: 'giwahs-stripe-on-dark.png',
    why: 'Appears on hosted invoice pages, checkout, customer portal, receipt PDFs. On-dark variant preserves the canonical black plate.',
    priority: 'P0',
  },
  {
    surface: 'Stripe Checkout · Brand colours',
    path: 'Dashboard → Settings → Business settings → Branding → Brand colour + Accent colour',
    file: 'No file — set colours only',
    why: 'Brand colour #0A0A0A · Accent colour #DAA520 (PAYMENTS_LOGO_V1 gold). Keeps checkout chrome on-brand.',
    priority: 'P0',
  },
  {
    surface: 'Stripe Customer Portal',
    path: 'Dashboard → Settings → Billing → Customer portal → Branding',
    file: 'giwahs-stripe-on-dark.png',
    why: 'Customers manage subscriptions and refunds here. Same wide on-dark variant for consistency.',
    priority: 'P1',
  },
  {
    surface: 'Stripe Receipts / payment-success emails',
    path: 'Inherits from Stripe Branding · Logo above',
    file: 'giwahs-stripe-on-dark.png',
    why: 'Stripe re-uses the Branding logo for transactional emails. No separate upload required.',
    priority: 'P1',
  },
  {
    surface: 'Stripe Invoice PDFs',
    path: 'Inherits from Stripe Branding · Logo above',
    file: 'giwahs-stripe-on-dark.png',
    why: 'Same inheritance. If you want a distinct invoice-only mark, upload giwahs-stripe-1024.png to a Stripe-managed invoice template (advanced).',
    priority: 'P1',
  },
  {
    surface: 'Whaitiri Black Limited NZ-invoice template (pre-Stripe / D-071)',
    path: 'Founder-controlled Google Doc · header band',
    file: 'giwahs-stripe-on-light.png',
    why: 'Light-frame variant designed specifically for the invoice letterhead per D-071 §3.2.',
    priority: 'P0',
  },
  {
    surface: 'Outreach + transactional email templates (Resend / Gmail)',
    path: '_outreach/approval_pack.md headers · payment_confirmation_email body',
    file: 'giwahs-stripe-512.png',
    why: 'Safest baseline across Gmail, Outlook 2016+, Apple Mail, mobile clients. Light-weight at 77 KB.',
    priority: 'P0',
  },
];

const PriorityChip = ({ p }) => {
  const cls = p === 'P0'
    ? 'bg-[#DAA520] text-[#0A0A0A] border-[#A87A2F]'
    : 'bg-[#1F1F1F] text-[#C0C0C0] border-[#3A3A3A]';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.16em] border ${cls}`}>
      {p}
    </span>
  );
};

const AssetCard = ({ asset }) => {
  const href = `${BASE}/${asset.file}`;
  return (
    <article
      data-testid={`asset-card-${asset.file}`}
      className="flex flex-col gap-4 border border-black bg-white p-5"
    >
      <div className={`aspect-[3/2] w-full overflow-hidden border border-neutral-300 ${asset.bgClass}`}>
        <img src={href} alt={asset.label} className="h-full w-full object-contain" />
      </div>
      <div>
        <div className="font-display text-base" style={{ fontWeight: 700 }}>{asset.label}</div>
        <div className="mt-1 font-mono-data text-[10px] uppercase tracking-wider text-neutral-500">{asset.spec}</div>
      </div>
      <p className="text-xs leading-relaxed text-neutral-600">{asset.purpose}</p>
      <div className="mt-auto flex flex-col gap-2 border-t border-neutral-200 pt-3">
        <div className="font-mono-data text-[10px] text-neutral-500 break-all">{asset.file}</div>
        <div className="flex gap-2">
          <a
            href={href}
            download={asset.file}
            data-testid={`download-${asset.file}`}
            className="inline-flex items-center gap-1.5 border border-black bg-[#0A0A0A] px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.14em] text-white hover:bg-[#DAA520] hover:text-[#0A0A0A] transition-colors"
          >
            <Download size={12} strokeWidth={1.8} /> Download
          </a>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`open-${asset.file}`}
            className="inline-flex items-center gap-1.5 border border-black bg-white px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.14em] text-[#0A0A0A] hover:bg-neutral-100"
          >
            Open <ExternalLink size={11} strokeWidth={1.8} />
          </a>
        </div>
      </div>
    </article>
  );
};

const PaymentsDownloadCentre = () => {
  const [copied, setCopied] = React.useState(null);
  const copyAll = () => {
    const lines = ASSETS.map(a => `${a.file}\t${window.location.origin}${BASE}/${a.file}`).join('\n');
    navigator.clipboard.writeText(lines).then(() => {
      setCopied(Date.now());
      setTimeout(() => setCopied(null), 2400);
    });
  };

  return (
    <div data-testid="payments-download-centre" className="bg-white">
      <SEO
        title="Payments Brand Assets · Download Centre"
        description="Direct download links and Stripe upload matrix for the GIWAHS payments lockup."
        noindex
      />

      <section className="border-b border-black bg-[#0A0A0A] text-white">
        <div className="mx-auto max-w-[1280px] px-5 py-16 md:px-8 md:py-20">
          <span className="label-overline text-neutral-500">/ Payments · Brand Assets</span>
          <h1
            data-testid="payments-download-headline"
            className="mt-4 font-display tracking-tighter"
            style={{ fontWeight: 700, fontSize: 'clamp(2rem, 5vw, 3.5rem)', lineHeight: 0.95 }}
          >
            PAYMENTS_LOGO_V1 · download centre.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-neutral-400">
            Eight payment-surface assets, every direct link verified at 200 OK, every file downloadable
            from the preview environment in one click. The Stripe upload matrix below tells you
            exactly which file goes into which Stripe surface.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              onClick={copyAll}
              data-testid="payments-copy-all"
              className="inline-flex items-center gap-2 border border-[#DAA520] bg-[#DAA520] px-4 py-2 text-xs font-mono uppercase tracking-[0.16em] text-[#0A0A0A] hover:bg-[#A87A2F] hover:text-white transition-colors"
            >
              {copied ? <CheckCircle2 size={14} strokeWidth={1.8} /> : <Copy size={14} strokeWidth={1.8} />}
              {copied ? 'Copied all 8 URLs' : 'Copy all 8 URLs'}
            </button>
            <a
              href={`${BASE}/giwahs-stripe-source.png`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-white px-4 py-2 text-xs font-mono uppercase tracking-[0.16em] text-white hover:bg-white hover:text-[#0A0A0A] transition-colors"
            >
              Verify source asset <ExternalLink size={12} strokeWidth={1.8} />
            </a>
          </div>
        </div>
      </section>

      {/* Assets grid */}
      <section className="border-b border-black">
        <div className="mx-auto max-w-[1280px] px-5 py-16 md:px-8 md:py-20">
          <span className="label-overline text-neutral-500">/ 01 — All eight assets, downloadable</span>
          <h2 className="mt-3 font-display text-2xl tracking-tighter md:text-4xl" style={{ fontWeight: 700 }}>
            Click any file to download.
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {ASSETS.map((a) => <AssetCard key={a.file} asset={a} />)}
          </div>
        </div>
      </section>

      {/* Stripe upload matrix */}
      <section className="border-b border-black bg-[#F5F5F0]">
        <div className="mx-auto max-w-[1280px] px-5 py-16 md:px-8 md:py-20">
          <span className="label-overline text-neutral-500">/ 02 — Stripe upload matrix</span>
          <h2 className="mt-3 font-display text-2xl tracking-tighter md:text-4xl" style={{ fontWeight: 700 }}>
            Exactly which file goes where.
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-neutral-700">
            Eight surfaces, each with the exact Stripe Dashboard path, the file to upload, and the
            reason why. Stripe is NOT yet activated — this is what to do the moment G4 is approved.
          </p>
          <div className="mt-10 overflow-x-auto">
            <table className="w-full min-w-[860px] border-collapse border border-black bg-white text-sm">
              <thead>
                <tr className="bg-[#0A0A0A] text-white">
                  <th className="px-4 py-3 text-left label-overline">Surface</th>
                  <th className="px-4 py-3 text-left label-overline">Stripe path</th>
                  <th className="px-4 py-3 text-left label-overline">File</th>
                  <th className="px-4 py-3 text-left label-overline">Why</th>
                  <th className="px-4 py-3 text-center label-overline">Priority</th>
                </tr>
              </thead>
              <tbody>
                {STRIPE_UPLOADS.map((row, i) => (
                  <tr key={i} data-testid={`stripe-row-${i}`} className="border-b border-neutral-200 align-top">
                    <td className="px-4 py-4 font-medium text-[#0A0A0A]">{row.surface}</td>
                    <td className="px-4 py-4 font-mono-data text-[11px] text-neutral-700">{row.path}</td>
                    <td className="px-4 py-4">
                      {row.file.startsWith('giwahs-') ? (
                        <a
                          href={`${BASE}/${row.file}`}
                          download={row.file}
                          className="font-mono-data text-[11px] text-[#0A0A0A] underline underline-offset-4 hover:text-[#A87A2F]"
                        >
                          {row.file}
                        </a>
                      ) : (
                        <span className="font-mono-data text-[11px] text-neutral-500">{row.file}</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-xs leading-relaxed text-neutral-700">{row.why}</td>
                    <td className="px-4 py-4 text-center"><PriorityChip p={row.priority} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* URL manifest */}
      <section className="border-b border-black bg-white">
        <div className="mx-auto max-w-[1280px] px-5 py-16 md:px-8 md:py-20">
          <span className="label-overline text-neutral-500">/ 03 — URL manifest (copy-paste)</span>
          <h2 className="mt-3 font-display text-2xl tracking-tighter md:text-4xl" style={{ fontWeight: 700 }}>
            Plain-text manifest.
          </h2>
          <div className="mt-8 overflow-x-auto bg-[#0A0A0A] text-white p-6 font-mono-data text-xs leading-relaxed whitespace-pre">
{ASSETS.map((a) => `${a.file}\n  ${BASE}/${a.file}`).join('\n\n')}
          </div>
          <p className="mt-4 text-xs text-neutral-500 font-mono">
            Append to <code>https://product-factory-hub.preview.emergentagent.com</code> in your browser, OR click any tile above to download directly.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-[1280px] px-5 md:px-8 py-10 font-mono-data text-xs text-neutral-500">
        Payment marks are provided for approved partner use under the GIWAHS Brand Guidelines.
      </div>
    </div>
  );
};

export default PaymentsDownloadCentre;
